import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';

import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { Text, TextType } from 'ui-components/components/text';

import styles from './searchable-dropdown.styles.module.less';
import { useIsUnitsOfMeasureAvailable } from '../../../../../../../feature-gates/use-is-units-of-measure-available';
import { messages } from '../messages';
import { usePurchaseOrderContext } from '../../../../purchase-order-context';

/**
 * @callback SetSearchResults
 * @param {object[]} results
 * @returns {void}
 */

/**
 * Component for displaying report actions
 * @param {object} props
 * @param {object[]} props.searchResults
 * @param {string} props.chosenItemId
 * @param {string} props.searchMessage
 * @param {React.Dispatch<React.SetStateAction<object[]>>} props.setSearchResults
 * @param {React.Dispatch<React.SetStateAction<string>>} props.setSearchTerm
 * @param {React.Dispatch<React.SetStateAction<string>>} props.setChosenItemId
 * @returns {JSX.Element}
 */

export const SearchableDropdown = ({
  searchResults,
  setSearchResults,
  setSearchTerm,
  chosenItemId,
  setChosenItemId,
  searchMessage,
  getNextRowKey,
}) => {
  const { setPurchaseOrder } = usePurchaseOrderContext();
  const { isAvailable: isUnitsOfMeasureEnabled } = useIsUnitsOfMeasureAvailable();
  const { formatMessage } = useIntl();

  useEffect(() => {
    if (chosenItemId === undefined) return;
    const chosenProduct = searchResults?.find((item) => `${item.productId}` === chosenItemId);
    if (!chosenProduct) return;

    let preselectedUomId = null;
    if (isUnitsOfMeasureEnabled) {
      if (chosenProduct._matchedUomSku) {
        preselectedUomId =
          chosenProduct.unitsOfMeasure?.find((u) => u.uomSku === chosenProduct._matchedUomSku)?.productUnitOfMeasureId ?? null;
      } else if (chosenProduct._matchedUomBarcode) {
        preselectedUomId =
          chosenProduct.unitsOfMeasure?.find((u) => u.barcode === chosenProduct._matchedUomBarcode)?.productUnitOfMeasureId ?? null;
      }
    }

    const mappedProduct = {
      productId: parseInt(chosenProduct.productId, 10),
      rowKey: getNextRowKey(chosenProduct.productId),
      name: chosenProduct.name,
      sku: chosenProduct.sku,
      supplierSku: '',
      quantity: 1,
      cost: 0,
      thumbnailUrl: chosenProduct.thumbnailUrl,
      unitsOfMeasure: isUnitsOfMeasureEnabled ? (chosenProduct.unitsOfMeasure ?? []) : null,
      unitOfMeasure: preselectedUomId ?? null,
    };

    setPurchaseOrder((order) => {
      const targetUomId = preselectedUomId ?? null;
      const existingIdx = (order.products || []).findIndex(
        (p) => p.productId === mappedProduct.productId && (!isUnitsOfMeasureEnabled || p.unitOfMeasure === targetUomId),
      );

      if (existingIdx !== -1) {
        return {
          ...order,
          products: order.products.map((p, i) => (i === existingIdx ? { ...p, quantity: p.quantity + 1 } : p)),
        };
      }
      return { ...order, products: [...(order.products || []), mappedProduct] };
    });

    setSearchTerm('');
    setSearchResults(undefined);
    setChosenItemId('');
  }, [
    chosenItemId,
    getNextRowKey,
    isUnitsOfMeasureEnabled,
    searchResults,
    setSearchTerm,
    setPurchaseOrder,
    setSearchResults,
    setChosenItemId,
  ]);

  const chooseProduct = (id) => {
    setChosenItemId(id);
  };

  return (
    <div className={styles['search-results']}>
      <div className={styles['result-dropdown-menu']}>
        {searchMessage ? <Text className={styles['result-title']} textType={TextType.Paragraph} value={searchMessage} /> : null}
        {searchResults?.map((item) => {
          let itemSkuLabel = item.sku;
          if (item._matchedUomSku) {
            itemSkuLabel = formatMessage(messages.uomSkuMatch, { sku: item.sku, uomSku: item._matchedUomSku });
          } else if (item._matchedUomBarcode) {
            itemSkuLabel = formatMessage(messages.uomBarcodeMatch, { sku: item.sku, uomBarcode: item._matchedUomBarcode });
          }
          return (
            <div key={`${item.productId}`} className={styles['result-item']}>
              <DropdownMenuItem className={styles.row} onClick={() => chooseProduct(`${item.productId}`)}>
                <Text className={styles['item-name']} textType={TextType.Paragraph} value={item.name} />
                <Text className={styles['item-sku']} textType={TextType.Caption} value={itemSkuLabel} />
              </DropdownMenuItem>
            </div>
          );
        })}
      </div>
    </div>
  );
};
